/**
 * operatorCallAccept — THE single Business Web implementation of the canonical operator-call accept.
 *
 * WHY IT IS EXTRACTED. The accept lived inside `UnifiedAgentDesktop`, so it existed only while
 * /unified-desktop was mounted. An encounter that rang on any other route fell through to the generic
 * staff path, which never sends this request: a live two-way video consultation whose server session
 * stayed `ringing` (ready to be finalised as missed/ring_timeout) and whose patient rendered as
 * "User <firebase_uid>". Runtime-proven 2026-09-07 on enc_appt_d215534166ed.
 *
 * The first attempt at a fix had the global alert NAVIGATE to /unified-desktop. That was wrong: the
 * alert's own Answer handler carries a prior fix — "we do NOT navigate; navigating away dropped the
 * document-level user-activation and blocked autoplay" — so navigating on the trusted click both broke
 * audio and made the operator hunt for a second Answer button. Extracting the accept lets the alert
 * accept IN PLACE while keeping exactly ONE implementation of
 * POST /operator-call/{business_id}/sessions/{session_id}/accept.
 *
 * Pure transport + decision: no React, no routing, no media. The caller supplies the request function
 * and the live connection id, and acts on the returned outcome.
 */

/**
 * @param {object} deps
 * @param {function} deps.request        (path, init) => Response — the app's authenticated fetch
 * @param {string}   deps.businessId     tenant brand id
 * @param {string}   deps.sessionId      operator-call session id
 * @param {string|null} deps.connectionId  the socket-REGISTERED live connection id
 * @returns {Promise<{outcome:'won'|'answered_elsewhere'|'no_connection'|'failed', status:number, data:object}>}
 */
export async function acceptOperatorCallSession({ request, businessId, sessionId, connectionId }) {
  if (!businessId || !sessionId) return { outcome: 'failed', status: 0, data: {} };

  // NAME THE SOCKET THAT WILL CARRY THE MEDIA.
  //
  // The backend still tolerates a bodyless accept so an older client keeps working, but on that path it
  // cannot exclude the winner from its own `answered_elsewhere` fan-out: the winner's WS cancel lands
  // mid-accept and tears down the media it just won (debugger-proven on Android, enc_appt_fbeecb87bfd3).
  // Refusing without an id is deliberate — no live socket means no media could flow anyway, so there is
  // nothing to lose by declining rather than ringing falsely.
  const connId = (typeof connectionId === 'string' ? connectionId.trim() : '') || '';
  if (!connId) return { outcome: 'no_connection', status: 0, data: {} };

  const resp = await request(
    `/api/v1/operator-call/${businessId}/sessions/${sessionId}/accept`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ connection_id: connId }),
    });
  const data = await resp.json().catch(() => ({}));

  if (resp.ok && data && data.won) return { outcome: 'won', status: resp.status, data };
  if (resp.status === 409) return { outcome: 'answered_elsewhere', status: 409, data };
  return { outcome: 'failed', status: resp.status, data };
}

/**
 * The media-room contract for a won accept. The room identity and the KIND of call are the SERVER's
 * statements (from the accept response), never the client's: a client that could choose the kind would
 * be able to open a camera on an audio-only consultation. The invite is a fallback only for an older
 * backend that omits them.
 */
export function mediaRoomContractFor(data, invite) {
  const d = data || {};
  const inv = invite || {};
  const session = d.session || {};
  return {
    call_id: d.call_id || d.call_session_id || inv.call_session_id,
    call_type: session.call_type || inv.call_type || 'voice',
    conversation_id: session.conversation_id || inv.conversation_id || null,
  };
}
